import React, { useEffect, useMemo, useState } from "react";
import { DayBookingSaveData } from "../../models/dayBooking/dayBookingSaveData";
import { useLocation, useParams } from "react-router-dom";
import { DayBookingForm } from "./dayBookingForm";
import { useAppSelector } from "../../framework/redux/hooks";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import {
    fetchDayBooking,
    fetchDayBookingTypes,
} from "../../framework/redux/slices/dayBookingSlice";
import { handleApiError } from "../../services/baseService";
import { LoadingIndicator } from "../framework/loadingIndicator";
import { useSelectDayBookingById } from "../../hooks/worktime/useSelectDayBookings";
import { DayBookingUsageType } from "../../models/common/enums";
import { Base } from "../../framework/common/base";
import { fetchCostCenters } from "../../framework/redux/slices/costCenterSlice";
import { fetchMyVehicles } from "../../framework/redux/slices/vehicleSlice";
import { useFilterWorkShiftTimeSlotsByPlannedShiftStart } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { fetchWorkShifts } from "../../framework/redux/slices/workShiftSlice";

export interface DayBookingFormLocationState {
    saveData: DayBookingSaveData;
}

export const DayBookingFormMain = () => {
    const dayBookingTypes = useAppSelector(
        ({ dayBooking }) => dayBooking.dayBookingTypes
    );
    const [dayBookingSaveData, setdayBookingSaveData] =
        useState<DayBookingSaveData>();
    const costCenters = useAppSelector((state) => state.costCenter.costCenterItems);
    const myVehicles = useAppSelector(({ vehicle }) => vehicle.myVehicles);
    const { date, id } = useParams();
    const location = useLocation();
    const state = location.state as DayBookingFormLocationState | undefined;
    const saveData = state?.saveData;

    const dateString = useMemo(
        () => date ?? Base.dateToDateJSON(new Date()),
        [date]
    );

    const workShiftTimeSlots = useFilterWorkShiftTimeSlotsByPlannedShiftStart(
        dateString,
        dateString
    );

    const firstPresentCostCenterId = useMemo(
        () => workShiftTimeSlots.map((s) => s.costCenterId).find((id) => id),
        [workShiftTimeSlots]
    );

    const firstPresentVehicleId = useMemo(
        () => workShiftTimeSlots.map((s) => s.vehicleId).find((id) => id),
        [workShiftTimeSlots]
    );

    useAsyncThunkAction(
        () =>
            fetchWorkShifts({
                startDate: dateString,
                endDate: dateString,
            }),
        {
            onError: handleApiError,
        },
        [dateString]
    );

    useAsyncThunkAction(() => fetchCostCenters(), { onError: handleApiError }, []);

    useAsyncThunkAction(
        () => fetchMyVehicles(),
        { onError: handleApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchDayBookingTypes(),
        { onError: handleApiError },
        []
    );

    const dayBooking = useSelectDayBookingById(id);

    useAsyncThunkAction(
        () => {
            if (id && id !== "automatic") return fetchDayBooking(id);
        },
        { onError: handleApiError },
        [id]
    );

    const isEdit = !!id;
    const editItemReady =
        dayBookingSaveData?.id === id ||
        (!!dayBookingSaveData && id === "automatic");

    useEffect(() => {
        // Init new day booking
        // When not initalized and id is not present
        if (!isEdit && !dayBookingSaveData && dayBookingTypes) {
            setdayBookingSaveData({
                id: "",
                dayBookingTypeId: dayBookingTypes[0]?.id,
                costCenterId: null,
                vehicleId: null,
                amount: 1,
                comment: "",
                day: dateString,
                endDate: dateString,
                rowId: "",
                dayUsageType: DayBookingUsageType.FullDay,
            });
        }
    }, [isEdit, dayBookingTypes]);

    useEffect(() => {
        // Init edit day booking
        // When id is present and current data doesn't match with id
        // And daybooking has been loaded
        if (isEdit && !editItemReady && dayBooking) {
            const {
                id,
                rowId,
                dayBookingTypeId,
                costCenterId,
                vehicleId,
                amount,
                comment,
                day,
                endDay,
                dayUsageType,
            } = dayBooking;
            setdayBookingSaveData({
                id,
                rowId,
                dayBookingTypeId,
                costCenterId,
                vehicleId,
                amount,
                comment,
                day,
                dayUsageType,
                endDate: endDay,
            });
        }
    }, [id, dayBooking]);

    useEffect(() => {
        // Init edit item from saveData
        // When editing automatic daily allowances,
        // item will be provided in location.state
        if (!!saveData) {
            setdayBookingSaveData(saveData);
        }
    }, [saveData]);

    if (!dayBookingSaveData || !dayBookingTypes || (isEdit && !editItemReady))
        return <LoadingIndicator />;

    return (
        <DayBookingForm
            dayBooking={dayBookingSaveData}
            dayBookingTypes={dayBookingTypes}
            costCenters={costCenters}
            vehicles={myVehicles}
            costCenterId={firstPresentCostCenterId}
            vehicleId={firstPresentVehicleId}
        />
    );
};
